/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { useLanguage, type Language } from '../contexts/LanguageContext';
import { m, AnimatePresence } from 'motion/react';
import {
  ArrowRight,
  Lightbulb,
  ShieldCheck,
  ChevronDown,
  ChevronsDownUp,
  ChevronsUpDown,
  BookOpen,
  TrendingUp,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useImageMappings } from '../hooks/useImageMappings';
import { useIsMobile } from '../hooks/useIsMobile';
import { IMAGE_SIZES, responsiveImageProps } from '../lib/imageVariants.js';
import { renderTextWithLinks } from '../lib/textUtils';
import { getComparerHref, type CompareLanguage } from '../lib/compareAnchor';
import { normalizeExampleVars } from '../lib/calcExamples';
import { formatErePrice, formatMonthYear } from '../lib/formatters';
import { cn } from '../lib/cn';
import { currentPageSurface, splitLanguageFromPath, trackEvent } from '../lib/gtag';

// ─── Mini FAQ item used only on homepage ─────────────────────────────────────
interface MiniItemProps {
  question: string;
  answer: string;
  /** 0-based position in the list — faq_index on the expand/collapse events. */
  index?: number;
  highlighted?: boolean;
  collapseKey?: number;
  expandKey?: number;
  onToggle?: (open: boolean) => void;
}

const MiniItem: React.FC<MiniItemProps> = ({
  question,
  answer,
  index,
  highlighted,
  collapseKey,
  expandKey,
  onToggle,
}) => {
  const [open, setOpen] = useState(false);
  const panelId = React.useId();
  useEffect(() => {
    setOpen(false);
  }, [collapseKey]);
  useEffect(() => {
    if (expandKey) setOpen(true);
  }, [expandKey]);
  const handleToggle = () => {
    const next = !open;
    // Same event names + shape as the CMS FAQ blocks and FAQAccordion — one
    // interaction, one event. Which homepage doubts get opened prioritises
    // copy work on the page where nearly all revenue clicks happen.
    trackEvent(next ? 'faq_expand' : 'faq_collapse', {
      faq_index: index,
      faq_question: question.slice(0, 80),
      surface: currentPageSurface(),
      language: splitLanguageFromPath(window.location.pathname).language,
    });
    setOpen(next);
    onToggle?.(next);
  };
  return (
    <div
      className={cn(
        'border-b border-slate-700/60 last:border-0',
        highlighted && 'bg-emerald-900/10'
      )}
    >
      <button
        onClick={handleToggle}
        aria-expanded={open}
        aria-controls={panelId}
        className="w-full py-5 flex items-center justify-between text-left gap-4 group focus-ring rounded-lg"
      >
        <span
          className={cn(
            'text-base font-black tracking-tight transition-colors',
            open ? 'text-emerald-500' : 'text-white group-hover:text-emerald-500'
          )}
        >
          {highlighted && (
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 mr-2.5 align-middle" />
          )}
          {question}
        </span>
        <ChevronDown
          className={cn(
            'w-5 h-5 text-fg-muted transition-transform duration-300 shrink-0',
            open && 'rotate-180 text-emerald-500'
          )}
        />
      </button>
      {/* The answer stays MOUNTED while collapsed (height 0 + inert), never
          conditionally rendered: the prerender bakes this section into
          static HTML, and a `{open && …}` panel leaves only the question in
          the DOM — crawlers and LLM readers without JS never saw an answer.
          `inert` keeps the collapsed copy out of the tab order and the
          accessibility tree; no `hidden`/`aria-hidden`, which text extractors
          (Readability & co.) treat as "drop this". */}
      <m.div
        id={panelId}
        initial={false}
        animate={open ? { height: 'auto', opacity: 1 } : { height: 0, opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="overflow-hidden"
        inert={!open}
      >
        {/* <div>, not <p>: answers come from the WYSIWYG editor and may carry
            <p> blocks — a <p> inside a <p> is auto-closed by the HTML parser,
            so the SSR DOM would diverge from the client tree (hydration #418,
            the About-page bug). */}
        <div className="pb-5 text-slate-400 font-medium leading-relaxed text-sm">
          {renderTextWithLinks(answer)}
        </div>
      </m.div>
    </div>
  );
};

// ─── Peildatum, derived from the price's own record ──────────────────────────
// `how_it_works.example_vars.ere_price_asof` is an ISO year-month (`2026-08`),
// deliberately NOT prose: the admin save mirrors the whole `example_vars`
// object byte-identically across NL and EN (useAdminData), so a localized
// month name would end up on the wrong language. The month is therefore
// rendered here, per site language.
//
// Amsterdam-pinned like every other date on the site (formatters.ts,
// SITE_TIME_ZONE): the prerender runs in UTC and hydration happens in the
// visitor's zone, and `new Date('2026-08-01')` is UTC midnight — without the
// pin it renders "juli 2026" for anyone west of UTC, which is a text
// hydration mismatch (React #418) on a prerendered route. The month name comes
// from the shared table too (formatMonthYear), never from the visitor's ICU.
const formatPriceAsOf = (asOf: unknown, language: Language): string => {
  if (typeof asOf !== 'string' || !/^\d{4}-(0[1-9]|1[0-2])$/.test(asOf)) return '';
  // Midday UTC, not midnight: the Amsterdam pin shifts the instant forward, and
  // a midnight instant on the 1st would still land on the 1st — but midday
  // leaves no room for a DST edge to move the month.
  return formatMonthYear(new Date(`${asOf}-01T12:00:00Z`), language);
};

// The reading in force carries its own date. The price series overlay
// (applyErePriceToContent in src/lib/erePrice.js) replaces the price and the
// band inside `example_vars`, but it writes the date of that reading beside
// them as `how_it_works.price_indicator_asof_iso` (YYYY-MM-DD), and leaves the
// CMS year-month alone. On prd that CMS field said 2026-08 while the card
// showed the 23 September reading, so the card stated a price from one date
// under the peildatum of another. The reading's own date therefore wins, and
// the year-month is only the fallback for an environment without a series:
// the same rule the Kennisbank benchmark follows. formatMonthYear pins the
// date to Amsterdam, so the bake and the hydrating client agree on the month.
const formatReadingMonth = (iso: unknown, language: Language): string =>
  typeof iso === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(iso) ? formatMonthYear(iso, language) : '';

// ─── Card CTA ────────────────────────────────────────────────────────────────
// A CMS-entered URL renders as a plain anchor (it may be external). Without one
// the card is a "go compare" CTA and sends the visitor to where the comparison
// starts (getComparerHref): Stap 1 in the hero from `sm`, the results on a
// phone. This section sits below the results, so that is a scroll up on the
// same route, which the link's `#start` hands to <ScrollToTop>.
const CardCta: React.FC<{
  href?: string;
  language: CompareLanguage;
  className: string;
  children: React.ReactNode;
}> = ({ href, language, className, children }) =>
  href ? (
    <a href={href} className={className}>
      {children}
    </a>
  ) : (
    <Link to={getComparerHref(language)} className={className}>
      {children}
    </Link>
  );

// ─── InfoSection ─────────────────────────────────────────────────────────────
export const InfoSection: React.FC = () => {
  const { language, t } = useLanguage();
  const { getImage } = useImageMappings();
  const isMobile = useIsMobile();
  const [collapseKey, setCollapseKey] = useState(0);
  const [expandKey, setExpandKey] = useState(0);
  const [openCount, setOpenCount] = useState(0);
  const handleToggleAll = () => {
    if (openCount > 0) {
      setCollapseKey((k) => k + 1);
      setOpenCount(0);
    } else {
      setExpandKey((k) => k + 1);
      setOpenCount(allFaqs.length);
    }
  };

  // ── The ERE price, from the ONE source ────────────────────────────────────
  // `how_it_works.example_vars` is the CMS-managed price the comparison, the
  // Kennisbank benchmark and every earnings estimate already calculate with
  // (normalizeExampleVars supplies lib/earnings.ts' fallback when the CMS key
  // is absent). The homepage used to carry the figure as prose baked into two
  // FAQ answers written in two different months — a July band in `faq.a2`, an
  // August one in `faq.a5` — so the page stated three prices at once, two of
  // them stale, and since the FAQ pairs are also baked as FAQPage JSON-LD the
  // stale ones were machine-readable too. Both answers now carry no figure and
  // point here instead; this card is the only place on the page that names a
  // price, and the number it names is the one the comparison calculates with.
  // See src/components/__tests__/InfoSection.test.tsx, which pins "one price,
  // one band, one peildatum" so a future copy edit cannot quietly reintroduce
  // a second figure.
  const rawPriceVars = t('how_it_works.example_vars', { returnObjects: true });
  const priceVars = normalizeExampleVars(rawPriceVars);
  // formatErePrice, never `€${n}` or .toFixed(): "€ 0,445" in NL, "€0.445" in
  // EN — symbol placement and decimal separator follow the site language, and
  // the reading keeps its own precision, so the card names the exact number
  // the comparison multiplies with.
  const erePriceLabel = formatErePrice(priceVars.ere_price_eur, language);
  const ereLowLabel = formatErePrice(priceVars.ere_price_low_eur, language);
  const ereHighLabel = formatErePrice(priceVars.ere_price_high_eur, language);
  // The peildatum that belongs to that price — read from the SAME object, not
  // from a sibling prose key. `how_it_works.price_indicator_date` was a free
  // text field one edit away from the price: the admin dashboard's "ERE-prijs"
  // quick action writes only `example_vars.ere_price_eur`, so a founder could
  // (and did) move the price while the date stayed on the previous month.
  // Living inside `example_vars` means the value the comparison calculates
  // with and the date it is true on are one record, edited in one place.
  // With a price series in place the reading's own date wins over that field
  // (see formatReadingMonth). Unset or malformed → no date line at all, never
  // a raw key path.
  const priceAsOf =
    formatReadingMonth(t('how_it_works.price_indicator_asof_iso'), language) ||
    formatPriceAsOf(
      (rawPriceVars as Record<string, unknown> | null | undefined)?.ere_price_asof,
      language
    );

  // Build FAQ list from all q/a pairs (top 5 highlighted on homepage)
  const faqData = t('faq', { returnObjects: true });
  const allFaqs: { question: string; answer: string }[] = [];
  if (faqData && typeof faqData === 'object') {
    Object.keys(faqData).forEach((key) => {
      if (key.startsWith('q')) {
        const num = key.replace('q', '');
        const question = faqData[key];
        const answer = faqData[`a${num}`];
        if (question && answer && typeof question === 'string' && typeof answer === 'string') {
          allFaqs.push({ question, answer });
        }
      }
    });
  }
  return (
    <section className="bg-transparent py-24 border-t border-slate-800 relative overflow-hidden">
      {/* Background visual — homepage slot, falls back to FAQ-page background
          so existing customizations keep working until an admin sets the new slot.
          Not on phones: the default slot image is a 325 KB webp shown at 15 %
          opacity through a blend mode — invisible on a phone screen, a real
          download on a phone connection, and a blend layer the GPU re-composes
          while the section scrolls. The server render and the first client
          render both include it (useIsMobile starts false), so hydration
          agrees; it is lazy-loaded and below the fold, so the one frame it is
          mounted on a phone never starts the fetch. */}
      {!isMobile && (
        <img
          {...responsiveImageProps(
            getImage('hero', 'info_bg') || getImage('faq', 'background'),
            IMAGE_SIZES.backdrop
          )}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-[0.15] pointer-events-none mix-blend-overlay"
          loading="lazy"
          decoding="async"
          aria-hidden="true"
        />
      )}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-16">
          <span className="inline-block text-xs font-black uppercase tracking-[0.3em] text-emerald-400 mb-4">
            {t('info.badge')}
          </span>
          <h2 className="text-3xl md:text-5xl font-black text-white mb-4">{t('info.title')}</h2>
          <p className="text-slate-400 text-lg font-medium">{t('info.subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-6 mb-12">
          {/* Mini FAQ accordion — left 2/3 */}
          <div className="lg:col-span-2">
            <div className="relative">
              <button
                onClick={handleToggleAll}
                className="absolute -top-3 right-8 z-10 group flex items-center gap-1.5 px-2 py-1 rounded-full bg-slate-800/80 border border-slate-700/50 text-fg-muted hover:text-emerald-500 hover:border-emerald-500/30 backdrop-blur-sm transition-all duration-300"
                title={openCount > 0 ? t('info.collapse_all') : t('info.expand_all')}
              >
                {openCount > 0 ? (
                  <ChevronsDownUp className="w-3 h-3 transition-transform duration-300 group-hover:scale-110" />
                ) : (
                  <ChevronsUpDown className="w-3 h-3 transition-transform duration-300 group-hover:scale-110" />
                )}
                <span className="text-[9px] font-bold uppercase tracking-wider max-w-0 overflow-hidden group-hover:max-w-[80px] transition-all duration-300 whitespace-nowrap">
                  {openCount > 0 ? t('info.collapse_all') : t('info.expand_all')}
                </span>
              </button>
              <div className="bg-slate-800/85 md:bg-slate-800/70 md:backdrop-blur-md border border-slate-700/50 rounded-3xl px-8 py-4 shadow-xl shadow-slate-900/30">
                {allFaqs.map((faq, i) => (
                  <MiniItem
                    key={i}
                    question={faq.question}
                    answer={faq.answer}
                    index={i}
                    highlighted={i < 5}
                    collapseKey={collapseKey}
                    expandKey={expandKey}
                    onToggle={(isOpen) => setOpenCount((c) => c + (isOpen ? 1 : -1))}
                  />
                ))}
                <AnimatePresence>
                  {openCount > 0 && (
                    <m.button
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      onClick={handleToggleAll}
                      className="w-full py-2 flex items-center justify-center text-slate-400 hover:text-slate-300 transition-colors"
                    >
                      <ChevronsDownUp className="w-3.5 h-3.5" />
                    </m.button>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Tip cards — right 1/3 */}
          <div className="space-y-6">
            <div className="bg-emerald-900/20 p-8 rounded-3xl border border-emerald-800/50 reveal-rise">
              <div className="w-12 h-12 bg-emerald-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-emerald-900/50">
                <Lightbulb className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-black text-emerald-300 [html.light_&]:text-emerald-700 mb-3">
                {t('info.home_tip_title')}
              </h3>
              {/* <div>, not <p>, around WYSIWYG copy (see the FAQ panel above):
                  this card's <p><span><p>… was the live hydration #418 on the
                  production homepage. */}
              <div className="text-emerald-200 [html.light_&]:text-emerald-700 text-sm leading-relaxed font-medium mb-6">
                {renderTextWithLinks(t('info.home_tip_text'))}
              </div>
              <CardCta
                language={language}
                className="text-emerald-300 [html.light_&]:text-emerald-700 text-sm font-black flex items-center gap-2 hover:gap-3 transition-all"
              >
                {t('info.home_compare_now')}
                <ArrowRight className="w-4 h-4" />
              </CardCta>
            </div>

            <div
              className="bg-slate-800/60 p-8 rounded-3xl border border-slate-700 reveal-rise"
              style={{ animationDelay: '100ms' }}
            >
              <div className="w-12 h-12 bg-emerald-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-emerald-900/50">
                <ShieldCheck className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-black text-white mb-3">
                {t('info.home_independent_title')}
              </h3>
              <div className="text-slate-400 text-sm leading-relaxed font-medium mb-4">
                {renderTextWithLinks(t('info.home_independent_text'))}
              </div>
              {/* The independence claim gets a checkable link, always. Label is
                  the existing `nav.how_we_compare` — the page's own name, and
                  already translated — rather than a new key nobody else uses;
                  the CMS-owned cta/cta_url pair below can't carry it, being
                  empty in the baseline and frozen in each env's i18n_admin. */}
              <Link
                to={`/${language}/hoe-wij-vergelijken`}
                className="text-emerald-400 [html.light_&]:text-emerald-700 text-sm font-black flex items-center gap-2 hover:gap-3 transition-all mb-6"
              >
                {t('nav.how_we_compare')}
                <ArrowRight className="w-4 h-4" />
              </Link>
              {t('info.home_independent_cta') && (
                <CardCta
                  href={t('info.home_independent_cta_url') || undefined}
                  language={language}
                  className="text-emerald-400 [html.light_&]:text-emerald-700 text-sm font-black flex items-center gap-2 hover:gap-3 transition-all"
                >
                  {t('info.home_independent_cta')}
                  <ArrowRight className="w-4 h-4" />
                </CardCta>
              )}
            </div>

            <div
              className="bg-slate-800/60 p-8 rounded-3xl border border-slate-700 reveal-rise"
              style={{ animationDelay: '200ms' }}
            >
              <div className="w-12 h-12 bg-emerald-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-emerald-900/50">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-black text-white mb-3">{t('info.home_market_title')}</h3>
              <div className="text-slate-400 text-sm leading-relaxed font-medium mb-4">
                {renderTextWithLinks(t('info.home_market_text'))}
              </div>
              {/* The number itself — one figure, one peildatum, straight from
                  the value the comparison calculates with. */}
              <dl className="mb-6 rounded-2xl border border-slate-700/70 bg-slate-900/50 px-4 py-3">
                <dt className="field-label mb-1">{t('info.ere_price_label')}</dt>
                <dd className="text-white font-black text-lg tabular-nums">
                  {t('info.ere_price_value').replace('{{price}}', erePriceLabel)}
                </dd>
                <dd className="text-slate-400 text-xs font-medium tabular-nums mt-0.5">
                  {t('info.ere_price_band')
                    .replace('{{low}}', ereLowLabel)
                    .replace('{{high}}', ereHighLabel)}
                </dd>
                {priceAsOf && (
                  <dd className="text-fg-muted text-[11px] font-medium mt-1">
                    {t('info.ere_price_asof').replace('{{date}}', priceAsOf)}
                  </dd>
                )}
              </dl>
              {t('info.home_market_cta') && (
                <CardCta
                  href={t('info.home_market_cta_url') || undefined}
                  language={language}
                  className="text-emerald-400 [html.light_&]:text-emerald-700 text-sm font-black flex items-center gap-2 hover:gap-3 transition-all"
                >
                  {t('info.home_market_cta')}
                  <ArrowRight className="w-4 h-4" />
                </CardCta>
              )}
            </div>
          </div>
        </div>

        {/* CTA banner to kennisbank */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-6 bg-slate-800/60 border border-slate-700 rounded-3xl px-8 py-6 reveal-rise">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-emerald-900/30 rounded-2xl flex items-center justify-center shrink-0">
              <BookOpen className="w-6 h-6 text-emerald-500" />
            </div>
            <div>
              <p className="text-white font-black text-lg leading-tight">
                {t('info.kennisbank_title')}
              </p>
              <p className="text-slate-400 text-sm font-medium">{t('info.kennisbank_text')}</p>
            </div>
          </div>
          <Link
            to={`/${language}/kennisbank`}
            className="shrink-0 px-8 py-3.5 bg-emerald-700 hover:bg-emerald-800 text-white font-black rounded-2xl transition-all shadow-lg shadow-emerald-900/20 flex items-center gap-2 group"
          >
            {t('info.kennisbank_cta')}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  );
};
